import React, { useState, useEffect } from 'react';
import billingService from '../../api/billingService';
import { formatCurrency } from '../../utils/formatters';

const RenewalModal = ({ show, onClose, subscription, onRenewalSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [customerQuotes, setCustomerQuotes] = useState([]);
  const [renewalData, setRenewalData] = useState({
    next_renewal_date: '',
    quotation_id: '',
    service_details: '',
    comment: '',
    isRenewal: true
  });

  useEffect(() => {
    if (show && subscription) {
      setRenewalData({
        next_renewal_date: subscription.next_renewal_date ? new Date(subscription.next_renewal_date).toISOString().split('T')[0] : '',
        quotation_id: '',
        service_details: subscription.service_details || '',
        comment: '',
        isRenewal: true
      });
      fetchQuotes();
    }
  }, [show, subscription]);

  const fetchQuotes = async () => {
    try {
      const customerId = subscription.customer_id || subscription.customerData?._id || subscription.customer?._id;
      if (customerId) {
        const res = await billingService.getQuotes({ customer_id: customerId });
        if (res.success) {
          // Filter for approved/active/accepted/signed quotes with single items
          const filteredQuotes = res.data.filter(q => 
            (q.status?.toLowerCase() === 'approved' || 
             q.status?.toLowerCase() === 'active'
            ));
          setCustomerQuotes(filteredQuotes);
        }
      }
    } catch (error) {
      console.error('Error fetching quotes for renewal:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!renewalData.quotation_id) {
      alert('Please attach a quotation for renewal.');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...renewalData,
        quotations: [renewalData.quotation_id], // Update quotations array
      };
      
      payload.next_renewal_date = new Date(payload.next_renewal_date).toISOString();
      
      const res = await billingService.updateSubscription(subscription._id, payload);
      if (res.success) {
        onRenewalSuccess();
        onClose();
        alert('Subscription renewed successfully!');
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header border-0 p-4">
            <h5 className="modal-title fw-bold">Renew Service: {subscription?.productData?.productName || subscription?.product?.productName}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body p-4 pt-0">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label text-muted small fw-600">PRODUCT (SELECTED)</label>
                  <input 
                    type="text" 
                    className="form-control bg-light border-0" 
                    value={subscription?.productData?.productName || subscription?.product?.productName || ''} 
                    disabled 
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-muted small fw-600">EXPIRY / RENEWAL DATE</label>
                  <input 
                    type="date" 
                    className="form-control" 
                    value={renewalData.next_renewal_date} 
                    onChange={e => setRenewalData({...renewalData, next_renewal_date: e.target.value})} 
                    required 
                  />
                </div>
                <div className="col-12">
                  <label className="form-label text-muted small fw-600">ATTACH QUOTATION (MANDATORY)</label>
                  <select 
                    className="form-select" 
                    value={renewalData.quotation_id} 
                    onChange={e => setRenewalData({...renewalData, quotation_id: e.target.value})} 
                    required
                  >
                    <option value="">Select Approved Quotation...</option>
                    {customerQuotes.length > 0 ? (
                      customerQuotes.map(q => (
                        <option key={q._id} value={q._id}>
                          {q.quotationNumber} - {formatCurrency(q.totalAmount)} ({q.items?.[0]?.productName || 'N/A'})
                        </option>
                      ))
                    ) : (
                      <option disabled>No approved single-item quotes found for this customer</option>
                    )}
                  </select>
                  <div className="text-xs text-muted mt-1">Only approved single-item quotations for this customer are listed.</div>
                </div>
                <div className="col-12">
                  <label className="form-label text-muted small fw-600">SERVICE DETAILS</label>
                  <textarea 
                    className="form-control" 
                    rows="3" 
                    value={renewalData.service_details} 
                    onChange={e => setRenewalData({...renewalData, service_details: e.target.value})}
                    placeholder="Technical details, server info, etc."
                  ></textarea>
                </div>
                <div className="col-12">
                  <label className="form-label text-muted small fw-600">RENEWAL COMMENT</label>
                  <textarea 
                    className="form-control" 
                    rows="2" 
                    value={renewalData.comment} 
                    onChange={e => setRenewalData({...renewalData, comment: e.target.value})}
                    placeholder="Add a note about this renewal..."
                  ></textarea>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 p-4">
              <button type="button" className="btn btn-light px-4" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary px-4" disabled={loading}>
                {loading ? 'Processing...' : 'Confirm Renewal'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RenewalModal;
